import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  Abandono,
  AbandonoDate,
  AbandonoFile,
  AbandonoId,
  AbandonoInfo,
  ActiveCustom,
  FileResponse,
  GetPedimentoFile,
  Pedimento,
  PedimentoByBatch,
  PedimentoCreate,
  PedimentoFile,
  PedimentoId,
  PedimentoResponse,
} from 'src/app/interfaces/abandonosPedimentos.interfaces';
import { GLOBAL } from 'src/app/_config/config';
import { MaterialGroup } from 'src/app/interfaces/lotes.interfaces';

/**
 * Servicio que gestiona los abandonos y pedimentos aduanales de los lotes,
 * tanto para operaciones de importación como de exportación, mediante el
 * consumo de los endpoints REST del backend.
 */
@Injectable({
  providedIn: 'root',
})
export class AbandonoPedimentoService {
  pedimentos: Pedimento[] = [];
  pedimentosFilter: Pedimento[] = [];
  abandonos: Abandono[] = [];
  abandonosFilter: Abandono[] = [];

  constructor(private http: HttpClient) {}

  // Abandoment

  /**
   * Obtiene la lista de abandonos asociados a un barco.
   * @param shipId - Identificador del barco.
   * @returns Observable con la lista de abandonos.
   */
  getAbandonoList(shipId: number): Observable<Abandono[]> {
    const url: string = `${GLOBAL.url}${GLOBAL.lotes.abandono.list}${shipId}`;
    return this.http.get<Abandono[]>(url);
  }

  /**
   * Obtiene la información de fecha de abandono de un lote.
   * @param idOperation - Identificador de la operación.
   * @param batchId - Identificador del lote.
   * @returns Observable con la información del abandono.
   */
  getAbandonoDate(idOperation: number, batchId: number): Observable<AbandonoInfo> {
    const url: string = `${GLOBAL.url}${GLOBAL.lotes.abandono.date}${idOperation}/${batchId}`;
    return this.http.get<AbandonoInfo>(url);
  }

  /**
   * Obtiene el archivo asociado a un abandono.
   * @param file - Datos del archivo de abandono a consultar.
   * @returns Observable con la respuesta del archivo.
   */
  getAbandonoFile(file: AbandonoFile): Observable<FileResponse> {
    const url: string = `${GLOBAL.url}${GLOBAL.lotes.abandono.file}`;
    return this.http.post<FileResponse>(url, file);
  }

  /**
   * Crea un nuevo registro de abandono.
   * @param abandono - Datos del abandono a crear.
   * @returns Observable con el identificador del abandono creado.
   */
  createAbandono(abandono: Abandono): Observable<AbandonoId> {
    const url: string = `${GLOBAL.url}${GLOBAL.lotes.abandono.create}`;
    return this.http.post<AbandonoId>(url, abandono);
  }

  /**
   * Actualiza un registro de abandono existente.
   * @param abandono - Datos del abandono a actualizar.
   * @returns Observable con el resultado de la operación.
   */
  updateAbandono(abandono: Abandono): Observable<{error: boolean, message: string }> {
    const url: string = `${GLOBAL.url}${GLOBAL.lotes.abandono.update}`;
    return this.http.put<{error: boolean, message: string }>(url, abandono);
  }

  /**
   * Elimina un registro de abandono.
   * @param abandono - Identificador del abandono a eliminar.
   * @returns Observable con la respuesta de la eliminación.
   */
  deleteAbandono(abandono: AbandonoId): Observable<{}> {
    const url: string = `${GLOBAL.url}${GLOBAL.lotes.abandono.delete}`;
    return this.http.delete<{}>(url, {
      headers: new HttpHeaders(),
      body: abandono,
    });
  }

  // Import Custom

  /**
   * Obtiene los tipos de solicitud de pedimento para importación.
   * @returns Observable con la lista de tipos de solicitud.
   */
  getCustomRequestType(): Observable<MaterialGroup[]> {
    const url: string = `${GLOBAL.url}${GLOBAL.lotes.pedimento.requestType}`;
    return this.http.get<MaterialGroup[]>(url);
  }

  /**
   * Obtiene el catálogo de claves de pedimento para importación.
   * @returns Observable con la lista de claves de pedimento.
   */
  getCustomKeyList(): Observable<MaterialGroup[]> {
    const url: string = `${GLOBAL.url}${GLOBAL.lotes.pedimento.keyList}`;
    return this.http.get<MaterialGroup[]>(url);
  }

  /**
   * Obtiene la lista de pedimentos activos de un lote de importación.
   * @param batchId - Identificador del lote.
   * @returns Observable con la lista de pedimentos activos.
   */
  getActiveCustomList(batchId: number): Observable<ActiveCustom[]> {
    const url: string = `${GLOBAL.url}${GLOBAL.lotes.pedimento.activeList}${batchId}`;
    return this.http.get<ActiveCustom[]>(url);
  }

  /**
   * Obtiene la lista completa de pedimentos de importación.
   * @returns Observable con la lista de pedimentos.
   */
  getPedimentoList(): Observable<Pedimento[]> {
    const url: string = `${GLOBAL.url}${GLOBAL.lotes.pedimento.list}`;
    return this.http.get<Pedimento[]>(url);
  }

  /**
   * Obtiene los pedimentos de importación de un lote específico.
   * @param batchId - Identificador del lote.
   * @returns Observable con la lista de pedimentos del lote.
   */
  getPedimentoListByBatch(batchId: number): Observable<Pedimento[]> {
    const url: string = `${GLOBAL.url}${GLOBAL.lotes.pedimento.get}${batchId}`;
    return this.http.get<Pedimento[]>(url);
  }

  /**
   * Crea un nuevo pedimento de importación.
   * @param pedimento - Datos del pedimento a crear.
   * @returns Observable con la respuesta de la creación.
   */
  createPedimento(pedimento: PedimentoCreate): Observable<PedimentoResponse> {
    const url: string = `${GLOBAL.url}${GLOBAL.lotes.pedimento.create}`;
    return this.http.post<PedimentoResponse>(url, pedimento);
  }

  /**
   * Sube el archivo asociado a un pedimento de importación.
   * @param pedimentoFile - Datos del archivo de pedimento a subir.
   * @returns Observable con la URL del archivo subido.
   */
  uploadPedimento(pedimentoFile: PedimentoFile): Observable<{url: ''}> {
    const url: string = `${GLOBAL.url}${GLOBAL.lotes.pedimento.uploadFile}`;
    return this.http.post<{url: ''}>(url, pedimentoFile);
  }

  /**
   * Descarga el archivo asociado a un pedimento de importación.
   * @param pedimentoFile - Datos del archivo de pedimento a descargar.
   * @returns Observable con el archivo del pedimento.
   */
  downloadPedimento(pedimentoFile: PedimentoFile): Observable<GetPedimentoFile> {
    const url: string = `${GLOBAL.url}${GLOBAL.lotes.pedimento.downloadFile}`;
    return this.http.post<GetPedimentoFile>(url, pedimentoFile);
  }

  /**
   * Actualiza un pedimento de importación existente.
   * @param pedimento - Datos del pedimento a actualizar.
   * @returns Observable con la respuesta de la actualización.
   */
  updatePedimento(pedimento: PedimentoCreate): Observable<PedimentoResponse> {
    const url: string = `${GLOBAL.url}${GLOBAL.lotes.pedimento.update}`;
    return this.http.put<PedimentoResponse>(url, pedimento);
  }

  /**
   * Elimina un pedimento de importación.
   * @param pedimento - Identificador del pedimento a eliminar.
   * @returns Observable con la respuesta de la eliminación.
   */
  deletePedimento(pedimento: PedimentoId): Observable<PedimentoResponse> {
    const url: string = `${GLOBAL.url}${GLOBAL.lotes.pedimento.delete}`;
    return this.http.delete<PedimentoResponse>(url, {
      headers: new HttpHeaders(),
      body: pedimento,
    });
  }

  // Export Custom

  /**
   * Obtiene la lista de pedimentos activos de exportación de un barco.
   * @param shipId - Identificador del barco.
   * @returns Observable con la lista de pedimentos activos.
   */
  getExportActiveCustomList(shipId: number): Observable<ActiveCustom[]> {
    const url: string = `${GLOBAL.url}${GLOBAL.loteExportacion.pedimento.activeList}${shipId}`;
    return this.http.get<ActiveCustom[]>(url);
  }

  /**
   * Obtiene la lista completa de pedimentos de exportación.
   * @returns Observable con la lista de pedimentos de exportación.
   */
  getExportPedimentoList(): Observable<Pedimento[]> {
    const url: string = `${GLOBAL.url}${GLOBAL.loteExportacion.pedimento.list}`;
    return this.http.get<Pedimento[]>(url);
  }

  /**
   * Obtiene los pedimentos de exportación de un barco específico.
   * @param shipId - Identificador del barco.
   * @returns Observable con la lista de pedimentos del barco.
   */
  getExportPedimentoListByShip(shipId: number): Observable<Pedimento[]> {
    const url: string = `${GLOBAL.url}${GLOBAL.loteExportacion.pedimento.listByShip}${shipId}`;
    return this.http.get<Pedimento[]>(url);
  }

  /**
   * Obtiene los pedimentos de exportación de un lote específico.
   * @param batchId - Identificador del lote.
   * @returns Observable con la lista de pedimentos por lote.
   */
  getExportPedimentoListByBatchId(batchId: number): Observable<PedimentoByBatch[]> {
    const url: string = `${GLOBAL.url}${GLOBAL.loteExportacion.pedimento.listByBatch}${batchId}`;
    return this.http.get<PedimentoByBatch[]>(url);
  }

  /**
   * Crea un nuevo pedimento de exportación.
   * @param pedimento - Datos del pedimento a crear.
   * @returns Observable con la respuesta de la creación.
   */
  createExportPedimento(pedimento: PedimentoCreate): Observable<PedimentoResponse> {
    const url: string = `${GLOBAL.url}${GLOBAL.loteExportacion.pedimento.create}`;
    return this.http.post<PedimentoResponse>(url, pedimento);
  }

  /**
   * Descarga el archivo asociado a un pedimento de exportación.
   * @param pedimentoFile - Datos del archivo de pedimento a descargar.
   * @returns Observable con el archivo del pedimento.
   */
  downloadExportPedimento(pedimentoFile: PedimentoFile): Observable<GetPedimentoFile> {
    const url: string = `${GLOBAL.url}${GLOBAL.loteExportacion.pedimento.downloadFile}`;
    return this.http.post<GetPedimentoFile>(url, pedimentoFile);
  }

  /**
   * Actualiza un pedimento de exportación existente.
   * @param pedimento - Datos del pedimento a actualizar.
   * @returns Observable con la respuesta de la actualización.
   */
  updateExportPedimento(pedimento: PedimentoCreate): Observable<PedimentoResponse> {
    const url: string = `${GLOBAL.url}${GLOBAL.loteExportacion.pedimento.update}`;
    return this.http.put<PedimentoResponse>(url, pedimento);
  }

  /**
   * Elimina un pedimento de exportación.
   * @param pedimento - Identificador del pedimento a eliminar.
   * @returns Observable con la respuesta de la eliminación.
   */
  deleteExportPedimento(pedimento: PedimentoId): Observable<PedimentoResponse> {
    const url: string = `${GLOBAL.url}${GLOBAL.loteExportacion.pedimento.delete}`;
    return this.http.delete<PedimentoResponse>(url, {
      headers: new HttpHeaders(),
      body: pedimento,
    });
  }
}
